import {t} from "@lingui/core/macro";
import {useCallback} from "react";
import {useLocation, useNavigate} from "react-router-dom-v5-compat";

import {emitGuideCommand} from "@repo/events-guide";
import {coeditingEditStreamChannel} from "@repo/guide-coeditor-core";
import {useResetConversation} from "@repo/guide-core";
import {setPromptVariable} from "@repo/guide-core-helpers";
import type {
    ActionPromiseCallbacks,
    ActionType,
    ActionTypeParameters,
    ActionTypeParametersCoeditorInsertText,
    ActionTypeParametersModal,
    ActionTypeParametersNavigation,
    ActionTypeParametersNewPrefilledChat,
    ActionTypeParametersSetPromptVariable,
} from "@repo/guide-types";
import {RequestInfo} from "@repo/shared-core";

// eslint-disable-next-line no-restricted-imports -- porting from old code.
import {emitCoeditingEdit} from "../deprecated-guide-coeditor/events.ts";

import {scrubTex, writeClipboardText} from "./copy-to-clipboard-helper.ts";

export type OpenModalActionButtonCallback = (
    modalID: string,
    parameters: ActionTypeParametersModal,
) => void;

interface MakeActionBubbleHandlersParams {
    openModal: OpenModalActionButtonCallback;
    asyncCallbacks: ActionPromiseCallbacks;
}

export type AsyncActionBubbleHandler = (ActionTypeParameters) => Promise<void>;

export type ActionBubbleHandler = (parameters: ActionTypeParameters) => void;

/**
 * Obtain a function to make custom handler functions for action bubbles.
 *
 * @returns A function to make custom handler functions for action bubbles.
 */
export const useMakeActionBubbleHandlers = (): ((
    params: MakeActionBubbleHandlersParams,
) => Record<ActionType, ActionBubbleHandler | AsyncActionBubbleHandler>) => {
    const navigate = useNavigate();
    const {search} = useLocation();
    const resetConversation = useResetConversation();

    const makeActionBubbleHandlers = useCallback(
        ({
            openModal,
            asyncCallbacks,
        }: MakeActionBubbleHandlersParams): Record<
            ActionType,
            ActionBubbleHandler | AsyncActionBubbleHandler
        > => {
            return {
                // sync-start:actionParameters 1379216153 https://github.com/Khan/webapp/blob/master/services/ai-guide/models/client_conversation_entry.go
                navigation: (parameters: ActionTypeParameters) => {
                    const p = parameters as ActionTypeParametersNavigation;
                    if (p.targetPath) {
                        const url = new URL(
                            p.targetPath,
                            // Note: location.origin via useLocation is not accessible at
                            // this callsite so we'll use window.location.origin
                            // eslint-disable-next-line no-restricted-syntax -- required
                            window.location.origin,
                        );
                        if (RequestInfo.isMobileApp) {
                            // ensure we stay on mobile web view
                            url.searchParams.set("embedded", "1");

                            // If we're trying to navigate to learner home
                            // we should redirect to mobile learner home.
                            if (url.pathname === "/profile/me/courses") {
                                // We must directly set window.location to properly change the origin.
                                // We cannot use traditional & safer navigation setting like
                                // history.push because that does not allow changing origin.
                                // This is a deep link to the mobile app's home tab screen.
                                // @ts-expect-error - TS2322 - Type 'URL' is not assignable to type 'Location'.
                                // eslint-disable-next-line no-restricted-syntax -- required
                                window.location = new URL("khanacademy:///");
                                return;
                            }
                        }

                        // Must use relative path for navigation to work correctly.
                        navigate(url.pathname + url.search);
                    }
                },
                setPromptVariable: (parameters: ActionTypeParameters) => {
                    const p =
                        parameters as ActionTypeParametersSetPromptVariable;
                    if (p.promptVariable && p.value) {
                        setPromptVariable(p.promptVariable, p.value);
                    }
                },
                openModal: (parameters: ActionTypeParameters) => {
                    const p = parameters as ActionTypeParametersModal;
                    if (p.modalID) {
                        openModal(p.modalID, p);
                    }
                },
                coeditorInsertText: (parameters: ActionTypeParameters) => {
                    const p =
                        parameters as ActionTypeParametersCoeditorInsertText;
                    if (p.text) {
                        emitCoeditingEdit(p.text);
                    }
                },
                newChat: (parameters: ActionTypeParameters) => {
                    const queryParams = new URLSearchParams(search);
                    queryParams.delete("resumeThreadId");
                    navigate({search: queryParams.toString()}, {replace: true});

                    resetConversation(true);
                },
                newPrefilledChat: (parameters: ActionTypeParameters) => {
                    const p =
                        parameters as ActionTypeParametersNewPrefilledChat;
                    const prefilledInput = p?.prefilledInput ?? "";
                    if (!prefilledInput) {
                        return;
                    }
                    setPromptVariable("prefilledInput", prefilledInput);
                    resetConversation(true);
                },
                copyToClipboard: async (parameters: ActionTypeParameters) => {
                    const textToCopy = parameters["textToCopy"];
                    await writeClipboardText(scrubTex(textToCopy))
                        .then((_res) => {
                            asyncCallbacks.onActionResolved(t`Copied!`);
                        })
                        .catch((_err) => {
                            asyncCallbacks.onActionRejected(t`Not copied`);
                        });
                },
                replaceHighlightedText: (parameters: ActionTypeParameters) => {
                    const replacementText = parameters["replacementText"];
                    const edit = JSON.stringify({edit: replacementText});
                    emitGuideCommand({
                        name: coeditingEditStreamChannel,
                        content: edit,
                    });
                },
                // sync-end:actionParameters
            };
        },
        [navigate, search, resetConversation],
    );

    return makeActionBubbleHandlers;
};
